import { useResource } from 'app/state/resource'
import { statsResource } from 'features/account/state/accountSlice'
import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { QuotaCalculationParams as QuotaContext } from './quotas'
import { QuotaResult, QuotaState, QuotaTypes } from './types'
import { ApiError, FalsyValue } from 'features/common'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { isPublicViewUrl } from 'features/common/utils/path'

export function useQuotaContext(): QuotaContext {
  const accountData = useGetAccountData()
  const quotas = accountData?.quotas
  const location = useLocation()
  const enabled = !isPublicViewUrl(location.pathname)

  const { data: stats } = useResource(statsResource, enabled)

  return useMemo(
    () => ({
      stats,
      quotas
    }),
    [stats, quotas]
  )
}

export function getQuotaDefinitions<T extends QuotaTypes>(
  definitions: Partial<Record<T, (context: QuotaContext) => QuotaState | FalsyValue>>,
  context: QuotaContext
) {
  const quotaKeys = Object.keys(definitions) as T[]
  return quotaKeys.reduce((r, quota) => {
    const def = definitions[quota]
    const state = def && def(context)

    if (state) {
      r[quota] = state
    }
    return r
  }, {} as Record<T, QuotaState>)
}

export function calculateQuota(quota: QuotaTypes, state: QuotaState | FalsyValue) {
  if (!state || typeof state.current !== 'number' || typeof state.limit !== 'number') {
    return { ...state, type: quota, exceeded: false, hit: false }
  }
  const { current, limit } = state
  return {
    ...state,
    type: quota,
    hit: current >= limit,
    exceeded: current > limit
  }
}

export function calculateQuotas<T extends QuotaTypes>(states: Record<T, QuotaState>) {
  const quotaKeys = Object.keys(states) as T[]
  const quotaStats: Record<T, QuotaResult> = quotaKeys.reduce((r, quota) => {
    const state = states[quota]
    r[quota] = calculateQuota(quota, state)
    return r
  }, {} as Record<T, QuotaResult>)

  const values = Object.values<QuotaResult>(quotaStats)

  const getFirstQuotaHit = () => {
    return values.find((quota) => quota.hit)
  }
  const getFirstQuotaExceeded = () => {
    return values.find((quota) => quota.exceeded)
  }

  return {
    get anyQuotaExceed() {
      return values.some((state) => state?.exceeded)
    },
    get severalQuotasExceeded() {
      return values.filter((state) => state?.exceeded).length > 1
    },
    getFirstQuotaHit,
    getFirstQuotaExceeded,
    ...quotaStats
  }
}

export function additionWouldExceedQuota(quota?: QuotaState, addend?: number) {
  if (quota && typeof quota.current === 'number' && typeof quota.limit === 'number') {
    return quota.current + (addend || 1) > quota.limit
  }
  return undefined
}

export function isHardQuotaError(error?: ApiError | null) {
  return error?.status === 422
}

export function assertAddditionWouldNotExceedQuota(quota?: QuotaResult) {
  if (additionWouldExceedQuota()) {
    throw { status: 422, quota, message: "couldn't exceed hard quota" }
  }
}
